// Shared pieces: buttons, dialogs, toasts, confirm, fields, status chips.

import type { ComponentChildren, JSX } from 'preact';
import { useEffect, useRef, useState, useCallback } from 'preact/hooks';
import { Icon, type IconName } from './icons';
import { errorText } from './api';
import type { EmailStatus } from '../../shared/types';

// ---------- buttons ----------

type BtnKind = 'primary' | 'secondary' | 'quiet' | 'danger';

export function Button(
  props: { kind?: BtnKind; icon?: IconName; busy?: boolean; size?: 'm' | 'l' | 's'; children?: ComponentChildren } & Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, 'size' | 'icon'>,
) {
  const { kind = 'secondary', icon, busy, size = 'm', children, class: cls, disabled, ...rest } = props;
  return (
    <button {...rest} class={`btn btn-${kind} btn-${size} ${busy ? 'is-busy' : ''} ${cls || ''}`} disabled={disabled || busy} aria-busy={busy || undefined}>
      {busy ? <span class="spin" /> : icon ? <Icon name={icon} size={size === 's' ? 16 : 18} /> : null}
      {children && <span>{children}</span>}
    </button>
  );
}

export function IconButton({ icon, label, onClick, disabled, class: cls }: { icon: IconName; label: string; onClick?: (e: MouseEvent) => void; disabled?: boolean; class?: string }) {
  return (
    <button type="button" class={`icon-btn ${cls || ''}`} aria-label={label} title={label} onClick={onClick} disabled={disabled}>
      <Icon name={icon} size={18} />
    </button>
  );
}

export function Spinner({ label }: { label?: string }) {
  return (
    <div class="loading" role="status">
      <span class="spin spin-l" />
      {label && <span>{label}</span>}
    </div>
  );
}

// ---------- dialogs ----------

export function Modal({ title, onClose, children, wide, footer }: { title: string; onClose: () => void; children: ComponentChildren; wide?: boolean; footer?: ComponentChildren }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const key = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', key);
    document.body.classList.add('has-modal');
    const first = box.current?.querySelector<HTMLElement>('input, textarea, select, button.btn-primary');
    first?.focus();
    return () => {
      document.removeEventListener('keydown', key);
      document.body.classList.remove('has-modal');
      prev?.focus?.();
    };
  }, []);
  return (
    <div class="overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div class={`modal ${wide ? 'modal-wide' : ''}`} role="dialog" aria-modal="true" aria-label={title} ref={box}>
        <div class="modal-head">
          <h2>{title}</h2>
          <IconButton icon="x" label="Close" onClick={onClose} />
        </div>
        <div class="modal-body">{children}</div>
        {footer && <div class="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// ---------- toasts and confirm (one host, mounted in App) ----------

type Toast = { id: number; text: string; kind: 'ok' | 'bad' | 'info'; action?: { label: string; run: () => void } };
type Ask = { title: string; body?: ComponentChildren; ok: string; danger?: boolean; resolve: (v: boolean) => void };

let pushToast: (t: Omit<Toast, 'id'>) => void = () => {};
let pushAsk: (a: Ask) => void = () => {};

export function toast(text: string, kind: Toast['kind'] = 'ok', action?: Toast['action']): void {
  pushToast({ text, kind, action });
}

export function toastError(e: unknown): void {
  pushToast({ text: errorText(e), kind: 'bad' });
}

export function confirmBox(opts: { title: string; body?: ComponentChildren; ok: string; danger?: boolean }): Promise<boolean> {
  return new Promise((resolve) => pushAsk({ ...opts, resolve }));
}

export function Host() {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [ask, setAsk] = useState<Ask | null>(null);
  useEffect(() => {
    let n = 0;
    pushToast = (t) => {
      const id = ++n;
      setToasts((list) => [...list.slice(-2), { ...t, id }]);
      setTimeout(() => setToasts((list) => list.filter((x) => x.id !== id)), t.kind === 'bad' ? 7000 : 3800);
    };
    pushAsk = setAsk;
  }, []);
  const close = (v: boolean) => {
    ask?.resolve(v);
    setAsk(null);
  };
  return (
    <>
      <div class="toasts" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} class={`toast toast-${t.kind}`}>
            <Icon name={t.kind === 'bad' ? 'alert' : t.kind === 'ok' ? 'check' : 'sparkle'} size={18} />
            <span>{t.text}</span>
            {t.action && (
              <button
                class="toast-action"
                onClick={() => {
                  t.action!.run();
                  setToasts((l) => l.filter((x) => x.id !== t.id));
                }}
              >
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
      {ask && (
        <Modal
          title={ask.title}
          onClose={() => close(false)}
          footer={
            <>
              <Button kind="quiet" onClick={() => close(false)}>
                Cancel
              </Button>
              <Button kind={ask.danger ? 'danger' : 'primary'} onClick={() => close(true)}>
                {ask.ok}
              </Button>
            </>
          }
        >
          {ask.body && <div class="confirm-body">{ask.body}</div>}
        </Modal>
      )}
    </>
  );
}

// ---------- form pieces ----------

export function Field({ label, hint, error, children, right }: { label: string; hint?: ComponentChildren; error?: string | null; children: ComponentChildren; right?: ComponentChildren }) {
  return (
    <label class={`field ${error ? 'has-error' : ''}`}>
      <span class="field-label">
        {label}
        {right && <span class="field-right">{right}</span>}
      </span>
      {children}
      {error ? <span class="field-error">{error}</span> : hint ? <span class="field-hint">{hint}</span> : null}
    </label>
  );
}

export function Toggle({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label} class={`toggle ${on ? 'on' : ''}`} onClick={() => onChange(!on)}>
      <i />
    </button>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string; icon?: IconName }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div class="seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button type="button" role="radio" aria-checked={value === o.value} class={value === o.value ? 'on' : ''} onClick={() => onChange(o.value)} key={o.value}>
          {o.icon && <Icon name={o.icon} size={16} />}
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------- status ----------

const STATUS: Record<EmailStatus, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'chip-gray' },
  scheduled: { label: 'Scheduled', cls: 'chip-blue' },
  sending: { label: 'Sending', cls: 'chip-live' },
  paused: { label: 'Paused', cls: 'chip-amber' },
  sent: { label: 'Sent', cls: 'chip-green' },
  canceled: { label: 'Stopped', cls: 'chip-gray' },
  automated: { label: 'Automatic', cls: 'chip-blue' },
};

export function StatusChip({ status }: { status: EmailStatus }) {
  const s = STATUS[status];
  return (
    <span class={`chip ${s.cls}`}>
      {status === 'sending' && <i class="pulse" />}
      {s.label}
    </span>
  );
}

export function Progress({ value, max, label }: { value: number; max: number; label?: string }) {
  const p = max ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div class="progress" role="progressbar" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label}>
      <i style={{ width: `${p}%` }} />
    </div>
  );
}

export function Empty({ icon, title, children, action }: { icon: IconName; title: string; children?: ComponentChildren; action?: ComponentChildren }) {
  return (
    <div class="empty">
      <div class="empty-icon">
        <Icon name={icon} size={26} />
      </div>
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  );
}

export function Notice({ kind = 'info', icon, children, action }: { kind?: 'info' | 'warn' | 'bad' | 'ok'; icon?: IconName; children: ComponentChildren; action?: ComponentChildren }) {
  const ic: IconName = icon || (kind === 'ok' ? 'check' : kind === 'info' ? 'sparkle' : 'alert');
  return (
    <div class={`notice notice-${kind}`}>
      <Icon name={ic} size={18} />
      <div class="notice-text">{children}</div>
      {action}
    </div>
  );
}

// ---------- data loading ----------

/** Loads data, exposes reload, and shows server errors in place. */
export function useLoad<T>(fn: () => Promise<T>, deps: unknown[] = []): { data: T | null; error: string | null; loading: boolean; reload: () => Promise<void>; set: (d: T) => void } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const live = useRef(true);
  const reload = useCallback(async () => {
    try {
      const d = await fn();
      if (live.current) {
        setData(d);
        setError(null);
      }
    } catch (e) {
      if (live.current) setError(errorText(e));
    } finally {
      if (live.current) setLoading(false);
    }
  }, deps);
  useEffect(() => {
    live.current = true;
    setLoading(true);
    reload();
    return () => {
      live.current = false;
    };
  }, [reload]);
  return { data, error, loading, reload, set: setData };
}

export function LoadError({ error, retry }: { error: string; retry: () => void }) {
  return (
    <Notice kind="bad" action={<Button size="s" onClick={retry}>Try again</Button>}>
      {error}
    </Notice>
  );
}

/** Copy text and say so. */
export async function copyText(text: string, what = 'Copied'): Promise<void> {
  try {
    await navigator.clipboard.writeText(text);
    toast(what);
  } catch {
    toast('Could not copy. Select the text and copy it by hand.', 'bad');
  }
}
